import { useEffect, useState } from "react";
import axios from "axios";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent } from "@/components/ui/card";
import { ShoppingBag, Phone, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const EnterpriseMarketplaceOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/marketplace/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Pa kapab chaje kòmand Marketplace yo.");
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePayment = async (orderId) => {
    try {
      const response = await axios.post(`${API}/admin/marketplace/orders/${orderId}/approve`);
      if (response.data.success) {
        toast.success("Ou fenk konfime peman Moncash sa a! Machann lan fèk resevwa notifikasyon an.");
        fetchOrders();
      }
    } catch (error) {
      console.error(error);
      toast.error("Pa kapab verifye peman an.");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-HT', {
      style: 'decimal',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('fr-HT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <EnterpriseLayout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black text-[#232F3E] tracking-tighter italic uppercase leading-none">Market <span className="text-[#FF9900]">Order</span></h1>
            <p className="text-gray-400 font-bold uppercase tracking-widest text-[9px] mt-2">Lis konplè tout kòmand ki pase sou Marketplace la.</p>
          </div>
          <div className="bg-white px-6 py-4 rounded-2xl shadow-sm border border-gray-100">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Kòmand Total</p>
            <p className="text-2xl font-black text-[#FF9900]">{orders.length}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-12 h-12 border-4 border-[#FF9900] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <Card className="border-0 shadow-2xl rounded-[35px] overflow-hidden bg-white">
            <CardContent className="p-0">
              {orders.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                        <th className="py-6 px-10 text-left">Pwodwi</th>
                        <th className="py-6 px-8 text-left">Kliyan</th>
                        <th className="py-6 px-8 text-left">Machann</th>
                        <th className="py-6 px-8 text-left">Total Peye</th>
                        <th className="py-6 px-8 text-left">Machann Goud</th>
                        <th className="py-6 px-8 text-left">Sistèm Goud</th>
                        <th className="py-6 px-8 text-left">Estati</th>
                        <th className="py-6 px-8 text-left">Dat</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {orders.map((order) => (
                        <tr key={order.id} className="hover:bg-gray-50/80 transition-all border-b border-gray-50 last:border-0">
                          <td className="py-6 px-10">
                            <div className="flex flex-col">
                              <span className="font-black text-[#232F3E] text-sm tracking-tight">{order.product_title}</span>
                              <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">ID: {order.id.slice(0, 8)}</span>
                            </div>
                          </td>
                          <td className="py-6 px-8">
                            <div className="flex items-center gap-2 text-gray-700 font-bold">
                              <Phone className="w-3 h-3 text-gray-300" /> {order.buyer_phone}
                            </div>
                          </td>
                          <td className="py-6 px-8">
                            <span className="px-3 py-1 bg-gray-100 rounded-lg text-[10px] font-black text-gray-500 uppercase tracking-widest">
                                {order.seller_id?.slice(0, 8)}
                            </span>
                          </td>
                          <td className="py-6 px-8 font-black text-gray-900">
                            {formatPrice(order.price_htg)} HTG
                          </td>
                          <td className="py-6 px-8 font-black text-green-600">
                            {formatPrice(order.seller_amount_htg || 0)} HTG
                          </td>
                          <td className="py-6 px-8 font-black text-[#FF9900]">
                            {formatPrice(order.commission_htg || 0)} HTG
                          </td>
                          <td className="py-6 px-8">
                            {order.payment_status === 'completed' ? (
                                <span className="px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm bg-green-500 text-white shadow-green-100">
                                  Peye
                                </span>
                            ) : (
                                <div className="flex gap-2 items-center">
                                  <span className="px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm bg-amber-500 text-white shadow-amber-100">
                                    Atant
                                  </span>
                                  <Button 
                                    size="sm" 
                                    className="bg-blue-600 hover:bg-black text-white h-7 px-3 rounded-lg text-[9px] font-black uppercase tracking-widest shadow-md"
                                    onClick={() => handleApprovePayment(order.id)}
                                  >
                                    <Check className="w-3 h-3 mr-1" /> Apwouve Peman
                                  </Button>
                                </div>
                            )}
                          </td>
                          <td className="py-6 px-8 text-[11px] font-bold text-gray-400 uppercase">
                            {formatDate(order.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-32 text-gray-400">
                  <ShoppingBag className="w-20 h-20 mx-auto mb-6 opacity-10" />
                  <p className="font-black uppercase tracking-widest text-[11px]">Pa gen okenn kòmand Marketplace ankò.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </EnterpriseLayout>
  );
};
